import React, { useState, useEffect } from 'react';
import {
  User, FileText, Briefcase, GraduationCap, Award, Settings,
  Upload, Download, Edit, Trash2, Plus, Save, X, CheckCircle,
  AlertCircle, MapPin, Calendar, Clock, Building, Mail, Phone
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { candidateAPI, uploadAPI } from '../../utils/api';

const CandidatePortalDashboard = () => {
  const { user, logout, updateUserProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [candidateData, setCandidateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadCandidateData();
  }, []);

  const loadCandidateData = async () => {
    try {
      setLoading(true);
      const response = await candidateAPI.getProfile();
      setCandidateData(response.data.data);
    } catch (error) {
      setError('Failed to load profile data');
      console.error('Load candidate data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (message, type = 'success') => {
    if (type === 'success') {
      setSuccess(message);
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(message);
      setSuccess('');
      setTimeout(() => setError(''), 5000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'cv', label: 'Resume Upload', icon: FileText },
    { id: 'experience', label: 'Experience', icon: Briefcase },
    { id: 'education', label: 'Education', icon: GraduationCap },
    { id: 'skills', label: 'Skills', icon: Award },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img
                src="/appsytec-logo.png"
                alt="Appsytec Logo"
                className="h-8 w-auto object-contain mr-4"
              />
              <h1 className="text-2xl font-bold text-gray-900">Candidate Portal</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {candidateData?.first_name || user.email}
              </span>
              <button
                onClick={logout}
                className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-900"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 bg-white rounded-lg shadow-sm p-6 h-fit">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                      activeTab === tab.id
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 ml-8">
            <div className="bg-white rounded-lg shadow-sm">
              {activeTab === 'profile' && (
                <ProfileTab
                  candidateData={candidateData}
                  setCandidateData={setCandidateData}
                  showMessage={showMessage}
                />
              )}
              {activeTab === 'cv' && (
                <CVTab
                  candidateData={candidateData}
                  setCandidateData={setCandidateData}
                  showMessage={showMessage}
                  loadCandidateData={loadCandidateData}
                />
              )}
              {activeTab === 'experience' && (
                <ExperienceTab
                  candidateData={candidateData}
                  setCandidateData={setCandidateData}
                  showMessage={showMessage}
                  loadCandidateData={loadCandidateData}
                />
              )}
              {activeTab === 'education' && (
                <EducationTab
                  candidateData={candidateData}
                  setCandidateData={setCandidateData}
                  showMessage={showMessage}
                  loadCandidateData={loadCandidateData}
                />
              )}
              {activeTab === 'skills' && (
                <SkillsTab
                  candidateData={candidateData}
                  setCandidateData={setCandidateData}
                  showMessage={showMessage}
                  loadCandidateData={loadCandidateData}
                />
              )}
              {activeTab === 'settings' && (
                <SettingsTab
                  candidateData={candidateData}
                  showMessage={showMessage}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfileTab = ({ candidateData, setCandidateData, showMessage }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (candidateData) {
      setFormData({
        first_name: candidateData.first_name || '',
        last_name: candidateData.last_name || '',
        phone: candidateData.phone || '',
        current_location: candidateData.current_location || '',
        years_experience: candidateData.years_experience || '',
        current_job_title: candidateData.current_job_title || '',
        current_employer: candidateData.current_employer || '',
        expected_salary: candidateData.expected_salary || '',
        security_clearance: candidateData.security_clearance || '',
        notice_period: candidateData.notice_period || '',
        willing_to_relocate: candidateData.willing_to_relocate || false,
      });
    }
  }, [candidateData]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await candidateAPI.updateProfile(formData);
      setCandidateData(response.data.data);
      setEditing(false);
      showMessage('Profile updated successfully');
    } catch (error) {
      showMessage('Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-md hover:bg-gray-900 disabled:opacity-50"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setFormData({
                  first_name: candidateData.first_name || '',
                  last_name: candidateData.last_name || '',
                  phone: candidateData.phone || '',
                  current_location: candidateData.current_location || '',
                  years_experience: candidateData.years_experience || '',
                  current_job_title: candidateData.current_job_title || '',
                  current_employer: candidateData.current_employer || '',
                  expected_salary: candidateData.expected_salary || '',
                  security_clearance: candidateData.security_clearance || '',
                  notice_period: candidateData.notice_period || '',
                  willing_to_relocate: candidateData.willing_to_relocate || false,
                });
              }}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name
          </label>
          {editing ? (
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
            />
          ) : (
            <p className="text-gray-900">{candidateData?.first_name || 'Not provided'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name
          </label>
          {editing ? (
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
            />
          ) : (
            <p className="text-gray-900">{candidateData?.last_name || 'Not provided'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone
          </label>
          {editing ? (
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
            />
          ) : (
            <p className="text-gray-900">{candidateData?.phone || 'Not provided'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Location
          </label>
          {editing ? (
            <input
              type="text"
              value={formData.current_location}
              onChange={(e) => setFormData({ ...formData, current_location: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
            />
          ) : (
            <p className="text-gray-900">{candidateData?.current_location || 'Not provided'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Years of Experience
          </label>
          {editing ? (
            <input
              type="number"
              min="0"
              value={formData.years_experience}
              onChange={(e) => setFormData({ ...formData, years_experience: parseInt(e.target.value) || '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
            />
          ) : (
            <p className="text-gray-900">{candidateData?.years_experience || 'Not provided'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Job Title
          </label>
          {editing ? (
            <input
              type="text"
              value={formData.current_job_title}
              onChange={(e) => setFormData({ ...formData, current_job_title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
            />
          ) : (
            <p className="text-gray-900">{candidateData?.current_job_title || 'Not provided'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Security Clearance
          </label>
          {editing ? (
            <select
              value={formData.security_clearance}
              onChange={(e) => setFormData({ ...formData, security_clearance: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
            >
              <option value="">Select clearance level</option>
              <option value="None">None</option>
              <option value="BPSS">BPSS</option>
              <option value="CTC">CTC</option>
              <option value="SC">SC</option>
              <option value="DV">DV</option>
            </select>
          ) : (
            <p className="text-gray-900">{candidateData?.security_clearance || 'Not provided'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Expected Salary (£)
          </label>
          {editing ? (
            <input
              type="number"
              min="0"
              value={formData.expected_salary}
              onChange={(e) => setFormData({ ...formData, expected_salary: parseInt(e.target.value) || '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
            />
          ) : (
            <p className="text-gray-900">
              {candidateData?.expected_salary ? `£${candidateData.expected_salary.toLocaleString()}` : 'Not provided'}
            </p>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.willing_to_relocate}
              onChange={(e) => setFormData({ ...formData, willing_to_relocate: e.target.checked })}
              className="h-4 w-4 text-gray-800 focus:ring-gray-800 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">Willing to relocate</span>
          </label>
        </div>
      )}
    </div>
  );
};

const CVTab = ({ candidateData, showMessage, loadCandidateData }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileUpload = async (file) => {
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      showMessage('Please upload only PDF or DOCX files', 'error');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      showMessage('File size must be less than 10MB', 'error');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('cv', file);
      formData.append('is_primary', 'true');

      await uploadAPI.uploadCV(formData);
      showMessage('Resume uploaded successfully');
      loadCandidateData();
    } catch (error) {
      showMessage('Failed to upload resume', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
  };

  const downloadCV = async (cvId, filename) => {
    try {
      const response = await uploadAPI.downloadCV(cvId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showMessage('Failed to download resume', 'error');
    }
  };

  const deleteCV = async (cvId) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) return;

    try {
      await uploadAPI.deleteCV(cvId);
      showMessage('Resume deleted successfully');
      loadCandidateData();
    } catch (error) {
      showMessage('Failed to delete resume', 'error');
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Resume Management</h2>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center ${
          dragOver ? 'border-gray-800 bg-gray-50' : 'border-gray-300'
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
      >
        <Upload className="mx-auto h-12 w-12 text-gray-400" />
        <div className="mt-4">
          <p className="text-lg font-medium text-gray-900">Upload your resume</p>
          <p className="text-sm text-gray-600 mt-2">
            Drag and drop your resume here, or click to select
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Supports PDF and DOCX files up to 10MB
          </p>
        </div>
        <div className="mt-6">
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".pdf,.docx"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            <span className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-800 hover:bg-gray-900 disabled:opacity-50">
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Select File
                </>
              )}
            </span>
          </label>
        </div>
      </div>

      {/* Uploaded Resumes */}
      {candidateData?.cvs && candidateData.cvs.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Uploaded Resumes</h3>
          <div className="space-y-4">
            {candidateData.cvs.map((cv) => (
              <div key={cv.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center">
                  <FileText className="h-8 w-8 text-gray-400 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">{cv.original_filename}</p>
                    <p className="text-sm text-gray-600">
                      Uploaded {new Date(cv.created_at).toLocaleDateString()}
                      {cv.is_primary && <span className="ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded">Primary</span>}
                    </p>
                    <p className="text-xs text-gray-500">
                      Status: <span className={`font-medium ${
                        cv.parsing_status === 'completed' ? 'text-green-600' :
                        cv.parsing_status === 'failed' ? 'text-red-600' :
                        'text-yellow-600'
                      }`}>
                        {cv.parsing_status}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => downloadCV(cv.id, cv.original_filename)}
                    className="p-2 text-gray-600 hover:text-gray-900"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteCV(cv.id)}
                    className="p-2 text-red-600 hover:text-red-800"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ExperienceTab = ({ candidateData, showMessage, loadCandidateData }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    job_title: '',
    company: '',
    location: '',
    start_date: '',
    end_date: '',
    is_current: false,
    description: '',
    industry: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await candidateAPI.addExperience(formData);
      showMessage('Experience added successfully');
      setShowForm(false);
      setFormData({
        job_title: '',
        company: '',
        location: '',
        start_date: '',
        end_date: '',
        is_current: false,
        description: '',
        industry: ''
      });
      loadCandidateData();
    } catch (error) {
      showMessage('Failed to add experience', 'error');
    }
  };

  const deleteExperience = async (id) => {
    if (!window.confirm('Are you sure you want to delete this experience?')) return;

    try {
      await candidateAPI.deleteExperience(id);
      showMessage('Experience deleted successfully');
      loadCandidateData();
    } catch (error) {
      showMessage('Failed to delete experience', 'error');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Work Experience</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-md hover:bg-gray-900"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Experience
        </button>
      </div>

      {showForm && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry
                </label>
                <input
                  type="text"
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  disabled={formData.is_current}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800 disabled:bg-gray-100"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_current}
                  onChange={(e) => setFormData({ ...formData, is_current: e.target.checked, end_date: e.target.checked ? '' : formData.end_date })}
                  className="h-4 w-4 text-gray-800 focus:ring-gray-800 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">I currently work here</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your role and achievements..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-800 focus:border-gray-800"
              />
            </div>

            <div className="flex space-x-2">
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-md hover:bg-gray-900"
              >
                Add Experience
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Experience List */}
      <div className="space-y-4">
        {candidateData?.experience?.map((exp) => (
          <div key={exp.id} className="p-4 border border-gray-200 rounded-lg">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">{exp.job_title}</h3>
                <p className="text-gray-600">{exp.company}</p>
                {exp.location && (
                  <p className="text-sm text-gray-500 flex items-center mt-1">
                    <MapPin className="h-4 w-4 mr-1" />
                    {exp.location}
                  </p>
                )}
                <p className="text-sm text-gray-500 flex items-center mt-1">
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(exp.start_date).toLocaleDateString()} - {exp.is_current ? 'Present' : new Date(exp.end_date).toLocaleDateString()}
                </p>
                {exp.description && (
                  <p className="text-sm text-gray-700 mt-2">{exp.description}</p>
                )}
              </div>
              <button
                onClick={() => deleteExperience(exp.id)}
                className="p-2 text-red-600 hover:text-red-800"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}

        {(!candidateData?.experience || candidateData.experience.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            <Briefcase className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2">No work experience added yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Similar components for EducationTab, SkillsTab, and SettingsTab would follow the same pattern...
const EducationTab = ({ candidateData, showMessage, loadCandidateData }) => {
  // Implementation similar to ExperienceTab
  return <div className="p-6">Education section coming soon...</div>;
};

const SkillsTab = ({ candidateData, showMessage, loadCandidateData }) => {
  // Implementation similar to ExperienceTab
  return <div className="p-6">Skills section coming soon...</div>;
};

const SettingsTab = ({ candidateData, showMessage }) => {
  // Implementation for settings
  return <div className="p-6">Settings section coming soon...</div>;
};

export default CandidatePortalDashboard;